/**
 * SPA route-change tracker.
 *
 * Multi-page sites get a fresh rrweb Meta event on every full-page load, so
 * the replayer's Pages tab populates naturally. SPAs don't — `history.pushState`
 * is silent. This module monkey-patches the History API and listens for
 * `popstate` so every route change inside the same JS runtime emits a
 * `route-change` custom event on the rrweb stream. The replayer's
 * `derivePages` already consumes that event shape.
 *
 * Idempotent: `start()` only patches once per page; subsequent calls re-arm
 * the same callback. `stop()` restores the originals.
 */

export type RouteChangePayload = {
  url: string;
  path: string;
  title: string;
};

type HistoryFn = (data: unknown, unused: string, url?: string | URL | null) => void;

let originalPushState: HistoryFn | null = null;
let originalReplaceState: HistoryFn | null = null;
let popstateHandler: ((this: Window, ev: PopStateEvent) => void) | null = null;
let pageshowHandler: ((this: Window, ev: PageTransitionEvent) => void) | null = null;
let currentEmitter: ((payload: RouteChangePayload) => void) | null = null;
let currentHref = '';

function snapshot(): RouteChangePayload {
  return {
    url: typeof window !== 'undefined' ? window.location.href : '',
    path:
      typeof window !== 'undefined'
        ? window.location.pathname + window.location.search + window.location.hash
        : '',
    title: typeof document !== 'undefined' ? document.title : '',
  };
}

function emitIfChanged(): void {
  if (!currentEmitter || typeof window === 'undefined') return;
  const href = window.location.href;
  // Some routers call pushState with the same URL (e.g. on initial mount).
  // Suppress no-op transitions so the replayer's Pages tab stays clean.
  if (href === currentHref) return;
  currentHref = href;
  currentEmitter(snapshot());
}

/**
 * Begin tracking. Subsequent calls update the emitter without re-patching.
 * Safe to invoke before `document.body` exists.
 */
export function startRouteTracker(emit: (payload: RouteChangePayload) => void): void {
  if (typeof window === 'undefined' || typeof history === 'undefined') return;
  currentEmitter = emit;
  currentHref = window.location.href;

  if (originalPushState && originalReplaceState && popstateHandler) {
    // Already armed; just swapped the emitter above.
    return;
  }

  originalPushState = history.pushState.bind(history) as HistoryFn;
  originalReplaceState = history.replaceState.bind(history) as HistoryFn;

  history.pushState = function patchedPush(
    this: History,
    data: unknown,
    unused: string,
    url?: string | URL | null
  ): void {
    originalPushState!(data, unused, url);
    // Defer so consumers reading `location` inside their own pushState
    // sequencing see the new value before we emit.
    queueMicrotask(emitIfChanged);
  };

  history.replaceState = function patchedReplace(
    this: History,
    data: unknown,
    unused: string,
    url?: string | URL | null
  ): void {
    originalReplaceState!(data, unused, url);
    queueMicrotask(emitIfChanged);
  };

  popstateHandler = function () {
    emitIfChanged();
  };
  window.addEventListener('popstate', popstateHandler);

  // BFCache restore counts as a new page view — `pageshow` with persisted=true
  // means the browser brought the page back from the back/forward cache. The
  // path may differ from when we last emitted.
  pageshowHandler = function (ev: PageTransitionEvent) {
    if (ev.persisted) emitIfChanged();
  };
  window.addEventListener('pageshow', pageshowHandler);
}

/** Stop tracking and restore the original History methods. Idempotent. */
export function stopRouteTracker(): void {
  if (typeof window === 'undefined' || typeof history === 'undefined') return;
  if (originalPushState) {
    history.pushState = originalPushState as History['pushState'];
    originalPushState = null;
  }
  if (originalReplaceState) {
    history.replaceState = originalReplaceState as History['replaceState'];
    originalReplaceState = null;
  }
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler);
    popstateHandler = null;
  }
  if (pageshowHandler) {
    window.removeEventListener('pageshow', pageshowHandler);
    pageshowHandler = null;
  }
  currentEmitter = null;
  currentHref = '';
}
